import { Injectable, inject } from '@angular/core';
import { Device, DeviceType } from 'models/device';
import { map, tap } from 'rxjs/operators';
import { Observable } from 'rxjs';
import {
  CreateDeviceRequestV1,
  DevicesService,
  IPMappingService,
  LicenseService
} from '../swagger-api-v1';
import ProfileType = CreateDeviceRequestV1.ProfileTypeEnum;
import { DeviceLicenses } from 'models/device-licenses';
import { IpMapping } from 'models/ip-mapping';
import { DeviceIpMappings } from 'models/device-ip-mappings';

@Injectable({
  providedIn: 'root'
})
export class DeviceService {
  public readonly deviceTypeTranslations: Map<DeviceType, string> = new Map([
    [DeviceType.MOBILE_CHILD_PROTECTION, `DEVICES.TYPES.MOBILE_CHILD_PROTECTION`],
    [DeviceType.MOBILE, 'DEVICES.TYPES.MOBILE'],
    [DeviceType.ONNET, 'DEVICES.TYPES.ONNET'],
    [DeviceType.WINDOWS, 'DEVICES.TYPES.WINDOWS'],
    [DeviceType.DESKTOP, 'DEVICES.TYPES.DESKTOP'],
    [DeviceType.ONNET_MOBILE_CHILD_PROTECTION, 'DEVICES.TYPES.ONNET_MOBILE_CHILD_PROTECTION']
  ]);
  private deviceService = inject(DevicesService);
  private licenseService = inject(LicenseService);
  private ipMappingService = inject(IPMappingService);

  public getDeviceById$(id: number): Observable<Device> {
    return this.deviceService.getDeviceUsingGET(id).pipe(map((dto) => Device.fromDTO(dto)));
  }

  public getDevices$(): Observable<Device[]> {
    return this.deviceService
      .getDevicesUsingGET2()
      .pipe(map((result) => result.devices.map((device) => Device.fromDTO(device))));
  }

  public getIpMappingsByDeviceId$(deviceId: number): Observable<IpMapping[]> {
    return this.ipMappingService
      .getIpMappingUsingGET(deviceId)
      .pipe(map((dto) => dto.ips.map((ipDto) => IpMapping.fromDTO(ipDto))));
  }

  public saveIpMappingForDevice$(deviceId: number, ip: string): Observable<IpMapping[]> {
    return this.ipMappingService
      .createIpMappingUsingPOST(deviceId, { ip })
      .pipe(map((dto) => dto.ips.map((ipDto) => IpMapping.fromDTO(ipDto))));
  }

  public updateIpMapping$(ipMapping: IpMapping): Observable<IpMapping[]> {
    return this.ipMappingService
      .updateIpMappingUsingPATCH(ipMapping.id, { ip: ipMapping.ip })
      .pipe(map((dto) => dto.ips.map((ipDto) => IpMapping.fromDTO(ipDto))));
  }

  public deleteIpMapping$(ipMappingId: number): Observable<unknown> {
    return this.ipMappingService.deleteIpMappingUsingDELETE(ipMappingId);
  }

  public getDevicesByProfileId$(profileId: number): Observable<Device[]> {
    return this.deviceService
      .getDevicesUsingGET2(profileId)
      .pipe(map((result) => result.devices.map((device) => Device.fromDTO(device))));
  }

  public getDeviceIpMappings$(): Observable<DeviceIpMappings[]> {
    return this.ipMappingService.getIpMappingsUsingGET().pipe(
      map((result) => result.ipMappings.map((dto) => DeviceIpMappings.fromDTO(dto))),
      tap(() => {})
    );
  }

  public reassignDevices$(devices: Device[]): Observable<Device[]> {
    const deviceDTOs = devices.map((device) => device.toDTO());

    return this.deviceService
      .updateDevicesUsingPATCH({ devices: deviceDTOs })
      .pipe(map(() => devices));
  }

  public updateDevice$(device: Device): Observable<Device> {
    return this.deviceService
      .updateDevicesUsingPATCH({ devices: [device] })
      .pipe(map((res) => Device.fromDTO(res.devicesList[0])));
  }

  public deleteDevice$(deviceId: number): Observable<unknown> {
    return this.deviceService.deleteDeviceUsingDELETE(deviceId);
  }

  public createDevice$(device: Device, profileType?: ProfileType): Observable<Device> {
    const createDTO = {
      deviceType: device.type,
      deviceName: device.name.trim(),
      profileId: device.profileId,
      profileType
    } as CreateDeviceRequestV1;
    return this.deviceService
      .createDeviceUsingPOST(createDTO)
      .pipe(map((dto) => Device.fromDTO(dto)));
  }

  public getLicenses$(): Observable<DeviceLicenses> {
    return this.licenseService
      .getDevicesLicenseUsingGET2()
      .pipe(map((dto) => DeviceLicenses.fromDTO(dto)));
  }
}
